/* ============================================================
   優待チケット一覧（c-ticket-list）
   - <ticket-list> custom element
   - 日付解析(Asia/Tokyo) / 状態判定 / 開催日順 / ページネーション /
     URL同期 / 詳細モーダル(native dialog) / 顧客タグによるチケット交換
   - 状態は URL から読み直せる値(page)のみ保持する。
   ============================================================ */
(function () {
  'use strict';

  if (window.customElements && customElements.get('ticket-list')) return;

  var WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];
  var FIXED_SORT = 'date_asc';
  var DAY_MS = 86400000;
  var JST_OFFSET = 9 * 60 * 60 * 1000;
  var TEXT = {
    daysLeft: '開催まであと{days}日',
    today: '本日開催',
    ended: '開催終了',
    used: '使用済み',
    undated: '開催日未定',
    previousPage: '前のページ',
    nextPage: '次のページ',
    goToPage: '{page}ページ目へ移動',
    pageStatus: '全{total}ページ中 {current}ページ目を表示中',
    exchangeConfirm: '会場スタッフの前で交換をして下さい。この操作は取り消すことができません。',
    exchangeError: 'チケットの交換に失敗しました。通信環境を確認して、もう一度お試しください。',
    exchangeUsed: 'チケットは使用済みです',
    exchangeEnded: '開催終了'
  };

  var ARROW_PREV = '<svg width="10" height="18" viewBox="0 0 10 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.06 16.75l-8-8 8-8" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round"/></svg>';
  var ARROW_NEXT = '<svg width="10" height="18" viewBox="0 0 10 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M.75.75l8 8-8 8" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round"/></svg>';

  function isLeap(y) {
    return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  }

  /* YYYY/MM/DD HH:MM:SS を厳密に解析。実在しない日時は null。 */
  function parseDateTime(str) {
    if (!str) return null;
    var m = /^(\d{4})\/(\d{2})\/(\d{2}) (\d{2}):(\d{2}):(\d{2})$/.exec(String(str).trim());
    if (!m) return null;
    var y = +m[1], mo = +m[2], d = +m[3], h = +m[4], mi = +m[5], s = +m[6];
    if (mo < 1 || mo > 12) return null;
    if (h > 23 || mi > 59 || s > 59) return null;
    var dim = [31, isLeap(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (d < 1 || d > dim[mo - 1]) return null;
    // 与えられた日時は JST。その UTC エポック(ms)。
    var epoch = Date.UTC(y, mo - 1, d, h, mi, s) - JST_OFFSET;
    return { y: y, mo: mo, d: d, epoch: epoch };
  }

  /* エポック(ms)を JST の暦日番号へ変換（暦日差の算出用） */
  function jstDayNumber(epoch) {
    return Math.floor((epoch + JST_OFFSET) / DAY_MS);
  }

  function computeState(parsed, endParsed, nowEpoch) {
    if (!parsed) return { state: 'undated' };
    if (parsed.epoch <= nowEpoch) {
      if (endParsed && endParsed.epoch > parsed.epoch && nowEpoch < endParsed.epoch) {
        return { state: 'today' };
      }
      return { state: 'ended' };
    }
    var eventDay = jstDayNumber(parsed.epoch);
    var nowDay = jstDayNumber(nowEpoch);
    if (eventDay === nowDay) return { state: 'today' };
    return { state: 'upcoming', days: eventDay - nowDay };
  }

  function formatDate(parsed) {
    var wd = new Date(parsed.epoch + JST_OFFSET).getUTCDay();
    return parsed.mo + '/' + parsed.d + '（' + WEEKDAYS[wd] + '）';
  }

  function formatDateRange(parsed, endParsed) {
    var startDate = formatDate(parsed);
    if (!endParsed || endParsed.epoch <= parsed.epoch) return startDate;
    if (jstDayNumber(parsed.epoch) === jstDayNumber(endParsed.epoch)) return startDate;
    return startDate + '〜 ' + formatDate(endParsed);
  }

  function fill(tpl, map) {
    return String(tpl || '').replace(/\{(\w+)\}/g, function (_, k) {
      return map[k] != null ? map[k] : '';
    });
  }

  var TicketList = (function () {
    function TicketList() {
      return Reflect.construct(HTMLElement, [], TicketList);
    }
    TicketList.prototype = Object.create(HTMLElement.prototype);
    TicketList.prototype.constructor = TicketList;

    TicketList.prototype.connectedCallback = function () {
      if (this._inited) return;
      this._inited = true;

      this.grid = this.querySelector('.js-ticket-grid');
      this.dialog = this.querySelector('.js-ticket-dialog');
      this.dialogSlot = this.querySelector('.js-ticket-dialog-slot');
      this.paginationEl = this.querySelector('.js-ticket-pagination');
      this.statusEl = this.querySelector('.js-ticket-status');
      this.perPage = parseInt(this.getAttribute('data-per-page'), 10) || 8;
      this.routeUrl = this.getAttribute('data-route-url') || '';
      this.customerId = this.getAttribute('data-customer-id') || '';
      this._exchangePending = false;
      this._exchangeSucceeded = false;

      this.cards = Array.prototype.slice.call(this.querySelectorAll('.js-ticket-card'));

      this._bindModal();

      if (!this.grid || !this.cards.length) return;

      this.classList.add('is-ready');
      this._bindControls();
      this._computeAll();

      var raw = this._readUrl();
      this._apply(FIXED_SORT, raw.page, {});
      this._normalizeUrl(raw);

      this._startClock();
      this._openRequestedTicket();
    };

    TicketList.prototype.disconnectedCallback = function () {
      this._stopClock();
      if (this._onPop) window.removeEventListener('popstate', this._onPop);
    };

    /* ---------- 状態計算 ---------- */

    TicketList.prototype._computeCard = function (card, now) {
      var parsed = parseDateTime(card.getAttribute('data-datetime'));
      var endParsed = parseDateTime(card.getAttribute('data-end-datetime'));
      card._parsed = parsed;
      card._endParsed = endParsed;
      card._order = parseInt(card.getAttribute('data-order'), 10) || 0;
      card._stateInfo = computeState(parsed, endParsed, now == null ? Date.now() : now);
      this._applyCardState(card);
    };

    TicketList.prototype._computeAll = function () {
      var now = Date.now();
      var self = this;
      this.cards.forEach(function (card) {
        self._computeCard(card, now);
      });
    };

    TicketList.prototype._label = function (st) {
      if (st.state === 'today') return TEXT.today;
      if (st.state === 'ended') return TEXT.ended;
      if (st.state === 'undated') return TEXT.undated;
      return fill(TEXT.daysLeft, { days: st.days });
    };

    TicketList.prototype._applyState = function (root, card) {
      var st = card._stateInfo;
      var parsed = card._parsed;
      var endParsed = card._endParsed;
      root.setAttribute('data-state', st.state);

      var isUsed = card.getAttribute('data-used') === 'true';

      var band = root.querySelector('.js-ticket-band');
      var bandText = root.querySelector('.js-ticket-band-text');

      if (isUsed) {
        // 使用済みは日付状態より優先して表示する
        if (bandText) bandText.textContent = TEXT.used;
        if (band) band.hidden = false;
      } else if (st.state === 'undated') {
        var undatedLabel = card.getAttribute('data-undated-label');
        if (bandText) bandText.textContent = undatedLabel || '';
        if (band) band.hidden = !undatedLabel;
      } else {
        if (bandText) bandText.textContent = this._label(st);
        if (band) band.hidden = false;
      }

      var dateEl = root.querySelector('.js-ticket-date');
      if (dateEl) {
        if (parsed) {
          dateEl.textContent = formatDateRange(parsed, endParsed);
          dateEl.hidden = false;
        } else {
          dateEl.textContent = '';
          dateEl.hidden = true;
        }
      }
    };

    TicketList.prototype._applyCardState = function (card) {
      this._applyState(card, card);
    };

    /* ---------- 並べ替え ---------- */

    TicketList.prototype._sortCards = function (sort) {
      var cards = this.cards.slice();
      var upcoming = [], ended = [], undated = [];
      cards.forEach(function (c) {
        if (!c._parsed) undated.push(c);
        else if (c._stateInfo.state === 'ended') ended.push(c);
        else upcoming.push(c);
      });

      upcoming.sort(function (a, b) { return a._parsed.epoch - b._parsed.epoch; });
      // 開催終了は直近に終了したチケットから表示
      ended.sort(function (a, b) { return b._parsed.epoch - a._parsed.epoch; });
      undated.sort(function (a, b) { return a._order - b._order; });

      return upcoming.concat(undated, ended);
    };

    /* ---------- 表示反映 ---------- */

    TicketList.prototype._apply = function (sort, page, opts) {
      opts = opts || {};
      sort = FIXED_SORT;

      this.sortedCards = this._sortCards(sort);
      this.totalPages = Math.max(1, Math.ceil(this.sortedCards.length / this.perPage));

      page = parseInt(page, 10);
      if (!page || page < 1) page = 1;
      if (page > this.totalPages) page = this.totalPages;

      this.currentSort = sort;
      this.currentPage = page;

      this._renderPage();
      this._renderPagination();

      if (opts.announce) this._announce();
      if (opts.scroll) this._scrollToTop();
    };

    TicketList.prototype._renderPage = function () {
      var start = (this.currentPage - 1) * this.perPage;
      var end = start + this.perPage;
      var pageCards = this.sortedCards.slice(start, end);

      this.cards.forEach(function (c) { c.hidden = true; });

      var grid = this.grid;
      pageCards.forEach(function (c) {
        c.hidden = false;
        grid.appendChild(c);
      });
    };

    TicketList.prototype._pageParts = function (current, total) {
      var nums = [];
      for (var i = 1; i <= total; i++) {
        if (i === 1 || i === total || (i >= current - 1 && i <= current + 1)) nums.push(i);
      }
      var parts = [];
      var prev = 0;
      nums.forEach(function (n) {
        if (n - prev > 1) parts.push('ellipsis');
        parts.push(n);
        prev = n;
      });
      return parts;
    };

    TicketList.prototype._makeArrow = function (dir) {
      var isPrev = dir === 'prev';
      var target = isPrev ? this.currentPage - 1 : this.currentPage + 1;
      var disabled = isPrev ? this.currentPage <= 1 : this.currentPage >= this.totalPages;
      var cls = 'pagination__nav-item pagination__nav-item--arrow ' +
        (isPrev ? 'c-pagination__arrow-prev' : 'c-pagination__arrow-next') +
        (disabled ? ' pagination__nav-item--disabled' : '');
      var svg = isPrev ? ARROW_PREV : ARROW_NEXT;
      var label = isPrev ? TEXT.previousPage : TEXT.nextPage;

      var el;
      if (disabled) {
        el = document.createElement('span');
        el.setAttribute('aria-hidden', 'true');
      } else {
        el = document.createElement('a');
        el.href = this._buildUrl(this.currentSort, target);
        el.setAttribute('rel', isPrev ? 'prev' : 'next');
        el.setAttribute('data-page', target);
        el.setAttribute('aria-label', label);
      }
      el.className = cls;
      el.innerHTML = svg;
      return el;
    };

    TicketList.prototype._renderPagination = function () {
      if (!this.paginationEl) return;
      this.paginationEl.innerHTML = '';
      if (this.totalPages <= 1) return;

      var self = this;
      var nav = document.createElement('div');
      nav.className = 'pagination__nav';

      nav.appendChild(this._makeArrow('prev'));

      this._pageParts(this.currentPage, this.totalPages).forEach(function (p) {
        var item;
        if (p === 'ellipsis') {
          item = document.createElement('span');
          item.className = 'pagination__nav-item pagination__nav-item--ellipsis';
          item.textContent = '…';
        } else if (p === self.currentPage) {
          item = document.createElement('span');
          item.className = 'pagination__nav-item';
          item.setAttribute('aria-current', 'page');
          item.textContent = p;
        } else {
          item = document.createElement('a');
          item.className = 'pagination__nav-item';
          item.href = self._buildUrl(self.currentSort, p);
          item.setAttribute('data-page', p);
          item.setAttribute('aria-label', fill(TEXT.goToPage, { page: p }));
          item.textContent = p;
        }
        nav.appendChild(item);
      });

      nav.appendChild(this._makeArrow('next'));
      this.paginationEl.appendChild(nav);
    };

    /* ---------- URL ---------- */

    TicketList.prototype._readUrl = function () {
      var params = new URLSearchParams(location.search);
      return { sort: params.get('sort_by'), page: params.get('page') };
    };

    TicketList.prototype._buildUrl = function (sort, page) {
      var params = new URLSearchParams(location.search);
      params.delete('sort_by');
      if (page && page > 1) params.set('page', page);
      else params.delete('page');
      var qs = params.toString();
      return location.pathname + (qs ? '?' + qs : '') + location.hash;
    };

    TicketList.prototype._normalizeUrl = function (raw) {
      var need = false;
      if (raw.sort !== null) need = true;
      if (raw.page !== null) {
        var rp = parseInt(raw.page, 10);
        if (isNaN(rp) || rp !== this.currentPage) need = true;
      }
      if (need) {
        history.replaceState(this._historyState(), '', this._buildUrl(this.currentSort, this.currentPage));
      }
    };

    TicketList.prototype._historyState = function () {
      return { ticketList: this.getAttribute('data-section-id'), page: this.currentPage };
    };

    /* ---------- スクロール / 通知 ---------- */

    TicketList.prototype._scrollToTop = function () {
      var panel = this.closest('.c-ticket-list') || this;
      var hh = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
      var top = panel.getBoundingClientRect().top + window.pageYOffset - hh - 12;
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      try {
        window.scrollTo({ top: Math.max(top, 0), behavior: reduce ? 'auto' : 'smooth' });
      } catch (e) {
        window.scrollTo(0, Math.max(top, 0));
      }
    };

    TicketList.prototype._announce = function () {
      if (!this.statusEl) return;
      this.statusEl.textContent = fill(TEXT.pageStatus, {
        current: this.currentPage,
        total: this.totalPages
      });
    };

    /* ---------- イベント ---------- */

    TicketList.prototype._bindControls = function () {
      var self = this;

      if (this.paginationEl) {
        this.paginationEl.addEventListener('click', function (e) {
          var link = e.target.closest('a.pagination__nav-item');
          if (!link || !self.paginationEl.contains(link)) return;
          e.preventDefault();
          var page = parseInt(link.getAttribute('data-page'), 10);
          if (!page) return;
          history.pushState(self._historyState(), '', self._buildUrl(self.currentSort, page));
          self._apply(self.currentSort, page, { scroll: true, announce: true });
        });
      }

      this._onPop = function () {
        var raw = self._readUrl();
        self._apply(FIXED_SORT, raw.page, {});
      };
      window.addEventListener('popstate', this._onPop);
    };

    /* ---------- モーダル ---------- */

    TicketList.prototype._bindModal = function () {
      var self = this;

      if (this.grid) {
        this.grid.addEventListener('click', function (e) {
          var locationLink = e.target.closest('a.c-ticket-card__location-text');
          if (locationLink && self.grid.contains(locationLink)) return;

          var opener = e.target.closest('.js-ticket-open');
          if (!opener || !self.grid.contains(opener)) return;
          var card = opener.closest('.js-ticket-card');
          if (card) self._openModal(card, opener);
        });
      }

      if (!this.dialog) return;

      this.dialog.addEventListener('click', function (e) {
        var exchangeControl = e.target.closest('.c-ticket-modal__use');
        if (exchangeControl && self.dialog.contains(exchangeControl) && self._activeCard) {
          if (self._refreshExchangeState(exchangeControl)) {
            e.preventDefault();
            return;
          }
        }

        var useButton = e.target.closest('.js-ticket-use');
        if (useButton && self.dialog.contains(useButton)) {
          self._exchangeTicket(useButton);
          return;
        }

        if (self._exchangePending || self._exchangeSucceeded) return;

        if (e.target === self.dialog || e.target === self.dialogSlot) {
          self.dialog.close();
          return;
        }
        if (e.target.closest('.js-ticket-close')) self.dialog.close();
      });

      this.dialog.addEventListener('cancel', function (e) {
        if (self._exchangePending || self._exchangeSucceeded) e.preventDefault();
      });

      this.dialog.addEventListener('close', function () {
        if (self.dialogSlot) self.dialogSlot.innerHTML = '';
        if (self._lastFocus && typeof self._lastFocus.focus === 'function') {
          self._lastFocus.focus();
        }
        self._lastFocus = null;
        self._activeCard = null;
      });
    };

    TicketList.prototype._setExchangePending = function (pending, button) {
      this._exchangePending = pending;

      if (button) {
        if (pending) {
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
        } else {
          button.removeAttribute('aria-busy');
          button.disabled = !button.classList.contains('js-ticket-use');
        }
      }

      if (!this.dialog) return;
      var closes = this.dialog.querySelectorAll('.js-ticket-close');
      for (var i = 0; i < closes.length; i++) closes[i].disabled = pending;
    };

    TicketList.prototype._refreshExchangeState = function (control) {
      if (!this._activeCard) return false;

      this._computeCard(this._activeCard, Date.now());
      var modal = control.closest('.c-ticket-modal');
      if (modal) this._applyModalState(modal, this._activeCard);
      return this._activeCard._stateInfo.state === 'ended';
    };

    TicketList.prototype._exchangeTicket = function (button) {
      if (this._exchangePending || this._exchangeSucceeded) return;
      if (this._refreshExchangeState(button)) return;

      var tagName = button.getAttribute('data-tag-name') || '';
      if (!tagName || !this.routeUrl || !this.customerId) {
        this._showExchangeError(button);
        return;
      }

      if (!window.confirm(TEXT.exchangeConfirm)) return;
      if (this._refreshExchangeState(button)) return;

      var errorEl = button.closest('.c-ticket-modal__actions').querySelector('.js-ticket-exchange-error');
      if (errorEl) {
        errorEl.textContent = '';
        errorEl.hidden = true;
      }

      var self = this;
      this._setExchangePending(true, button);

      window.fetch(this.routeUrl + '/' + encodeURIComponent(this.customerId), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tagName: tagName })
      }).then(function (response) {
        if (!response.ok) throw new Error('Ticket exchange failed: ' + response.status);
        if (self.isConnected) self._showExchangeSuccess(button);
      }).catch(function (error) {
        console.error('チケット交換処理でエラーが発生しました:', error);
        if (self.isConnected) self._showExchangeError(button);
      });
    };

    TicketList.prototype._showExchangeError = function (button) {
      this._setExchangePending(false, button);

      var actions = button.closest('.c-ticket-modal__actions');
      var errorEl = actions ? actions.querySelector('.js-ticket-exchange-error') : null;
      if (errorEl) {
        errorEl.textContent = TEXT.exchangeError;
        errorEl.hidden = false;
      }

      try { button.focus({ preventScroll: true }); } catch (e) { button.focus(); }
    };

    TicketList.prototype._showExchangeSuccess = function (button) {
      this._setExchangePending(false, button);
      this._markTicketUsed(button);
      this._exchangeSucceeded = false;

      var close = this.dialog.querySelector('.js-ticket-close');
      if (close) try { close.focus({ preventScroll: true }); } catch (e) { close.focus(); }
    };

    TicketList.prototype._markTicketUsed = function (button) {
      if (!this._activeCard) return;

      this._activeCard.setAttribute('data-used', 'true');
      var cardOverlay = this._activeCard.querySelector('.js-ticket-used-overlay');
      if (cardOverlay) cardOverlay.hidden = false;

      // カード側の帯テキストを「使用済み」に更新
      this._applyCardState(this._activeCard);

      var modal = button.closest('.c-ticket-modal');
      if (modal) this._applyUsedState(modal, this._activeCard);
    };

    TicketList.prototype._applyUsedState = function (root, card) {
      var isUsed = card.getAttribute('data-used') === 'true';
      root.setAttribute('data-used', isUsed ? 'true' : 'false');

      var overlay = root.querySelector('.js-ticket-used-overlay');
      if (overlay) overlay.hidden = !isUsed;
      if (!isUsed) return;

      var useButton = root.querySelector('.c-ticket-modal__use');
      if (!useButton || useButton.tagName !== 'BUTTON') return;

      useButton.classList.add('c-ticket-modal__use--used');
      useButton.classList.remove('js-ticket-use');
      useButton.disabled = true;
      useButton.removeAttribute('data-tag-name');
      useButton.textContent = TEXT.exchangeUsed;
    };

    TicketList.prototype._applyEndedState = function (root, card) {
      if (card.getAttribute('data-used') === 'true' || card._stateInfo.state !== 'ended') return;

      var useButton = root.querySelector('.c-ticket-modal__use');
      if (!useButton) return;

      if (useButton.tagName !== 'BUTTON') {
        var disabledButton = document.createElement('button');
        disabledButton.type = 'button';
        disabledButton.className = useButton.className;
        useButton.parentNode.replaceChild(disabledButton, useButton);
        useButton = disabledButton;
      }

      useButton.classList.add('c-ticket-modal__use--ended');
      useButton.classList.remove('c-ticket-modal__use--login', 'c-ticket-modal__use--used', 'js-ticket-use');
      useButton.disabled = true;
      useButton.removeAttribute('href');
      useButton.removeAttribute('data-tag-name');
      useButton.removeAttribute('aria-busy');
      useButton.textContent = TEXT.exchangeEnded;
    };

    TicketList.prototype._applyModalState = function (modal, card) {
      this._applyState(modal, card);
      this._applyUsedState(modal, card);
      this._applyEndedState(modal, card);
    };

    TicketList.prototype._openModal = function (card, opener) {
      if (!this.dialog || !this.dialogSlot || !this.dialog.showModal) return;
      var tpl = card.querySelector('.js-ticket-template');
      if (!tpl || !('content' in tpl)) return;

      this._computeCard(card, Date.now());
      var frag = tpl.content.cloneNode(true);
      var modal = frag.querySelector('.c-ticket-modal');
      if (modal) this._applyModalState(modal, card);

      this.dialogSlot.innerHTML = '';
      this.dialogSlot.appendChild(frag);

      this._exchangePending = false;
      this._exchangeSucceeded = false;
      this._activeCard = card;
      this._lastFocus = opener || document.activeElement;
      this.dialog.showModal();

      // 現在表示されている閉じるボタン（PC=右上アイコン / SP=下部）へフォーカス
      var closes = this.dialog.querySelectorAll('.js-ticket-close');
      var focusTarget = null;
      for (var i = 0; i < closes.length; i++) {
        if (closes[i].offsetParent !== null) { focusTarget = closes[i]; break; }
      }
      if (!focusTarget) focusTarget = this.dialog;
      try { focusTarget.focus({ preventScroll: true }); } catch (e) { focusTarget.focus(); }
    };

    TicketList.prototype._normalizeComparableUrl = function (value) {
      if (!value) return '';
      try {
        var url = new URL(value, location.origin);
        return url.pathname.replace(/\/$/, '');
      } catch (e) {
        return String(value).split('?')[0].replace(/\/$/, '');
      }
    };

    TicketList.prototype._openRequestedTicket = function () {
      var params = new URLSearchParams(location.search);
      var ticketProductId = params.get('ticket_id');
      var eventUrl = this._normalizeComparableUrl(params.get('event'));
      if (!ticketProductId && !eventUrl) return;

      var target = this.cards.find(function (card) {
        if (ticketProductId && card.getAttribute('data-ticket-product-id') === ticketProductId) return true;
        return eventUrl && this._normalizeComparableUrl(card.getAttribute('data-event-url')) === eventUrl;
      }, this);

      if (target) this._openModal(target, null);
    };

    /* ---------- 1分ごとの再計算 ---------- */

    TicketList.prototype._startClock = function () {
      var self = this;
      this._clock = window.setInterval(function () {
        self._computeAll();
        if (self._activeCard && self.dialog && self.dialog.open) {
          var modal = self.dialog.querySelector('.c-ticket-modal');
          if (modal) self._applyModalState(modal, self._activeCard);
        }
        self._apply(self.currentSort, self.currentPage, {});
      }, 60000);
    };

    TicketList.prototype._stopClock = function () {
      if (this._clock) {
        window.clearInterval(this._clock);
        this._clock = null;
      }
    };

    return TicketList;
  })();

  customElements.define('ticket-list', TicketList);
})();
